import React from "react";
import { motion } from "framer-motion";
import { Reveal, StaggerGroup, StaggerChild } from "@/components/site/Reveal";

const CAPABILITIES = [
    {
        num: "01",
        pill: "Engage",
        title: "Conversational AI",
        body: "Human-like chat and voice assistants that understand intent, context and sentiment — in 22+ languages and dialects. Deploy across WhatsApp, web, mobile apps, IVR and contact centres, with seamless handover to human agents.",
        bullets: [
            "Voice bots with real-time speech recognition & natural TTS",
            "Omnichannel journeys with full conversation memory",
            "Conversation intelligence: sentiment, QA scoring & agent assist",
        ],
        mesh: "",
    },
    {
        num: "02",
        pill: "Act",
        title: "Agentic AI",
        body: "Beyond answering — Nyra agents get things done. They reason over goals, plan multi-step actions, call your APIs and complete workflows end-to-end: refunds, KYC, order changes, ticket triage, collections and more — autonomously, with guardrails.",
        bullets: [
            "Goal-driven planning with tool & API calling",
            "200+ prebuilt connectors: CRM, ERP, ITSM, payments",
            "Human-in-the-loop approvals & full audit trails",
        ],
        mesh: "mesh-b",
    },
    {
        num: "03",
        pill: "Orchestrate",
        title: "Multi-Agent AI",
        body: "Complex work needs a team. Nyra's orchestrator coordinates specialised agents — research, compliance, finance, support — that collaborate, delegate and verify each other's work. One supervisor, many experts, zero chaos.",
        bullets: [
            "Supervisor–specialist architecture with dynamic routing",
            "Shared memory & context handoff between agents",
            "Cross-verification agents for accuracy & policy compliance",
        ],
        mesh: "mesh-c",
    },
    {
        num: "04",
        pill: "Evaluate",
        title: "Nyra TenderIQ",
        body: "Built by us, for procurement and bid teams. TenderIQ ingests tenders, RFPs and bid documents of any size, extracts requirements and eligibility criteria, flags risks and deviations, and scores every bid against your evaluation matrix.",
        bullets: [
            "Requirement & clause extraction from 1000+ page documents",
            "Automated technical & commercial bid scoring, side-by-side",
            "Compliance checks, risk flags & go / no-go recommendations",
        ],
        mesh: "mesh-b",
    },
];

export const FourCapabilities = ({ openDemo }) => (
    <section className="pt-10 md:pt-14 pb-24 md:pb-28 bg-warm" data-testid="four-capabilities">
        <div className="container-x">
            <Reveal>
                <div className="mb-10 md:mb-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                    <div className="max-w-2xl">
                        <div className="text-eyebrow text-slate-500 mb-3">Four capabilities. One intelligence layer.</div>
                        <h2 className="heading-section text-3xl md:text-5xl">
                            <span className="text-slate-400">Most vendors give you one tool.</span>
                            <br />
                            Nyra AI gives you all of it.
                        </h2>
                    </div>
                    <p className="text-intro text-slate-600 text-base md:text-lg lg:max-w-md">
                        Built on one core, sharing one memory, governed by one console — so knowledge from a
                        support chat improves your agents, and agents built for operations power your tender flows.
                    </p>
                </div>
            </Reveal>

            <div className="space-y-6">
                {CAPABILITIES.map((c) => (
                    <Reveal key={c.num}>
                        <div className="gleap-card overflow-hidden" data-testid={`capability-${c.num}`} style={{ padding: 0 }}>
                            <div className="grid md:grid-cols-12 gap-0">
                                <div className="md:col-span-7 p-8 md:p-10">
                                    <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">
                                        <span
                                            className="font-serif text-2xl text-slate-900"
                                        >
                                            {c.num}
                                        </span>
                                        <span>·</span>
                                        <span className="text-slate-900 font-medium">{c.pill}</span>
                                    </div>
                                    <h3
                                        className="heading-section text-3xl md:text-4xl text-slate-900"
                                    >
                                        {c.title}
                                    </h3>
                                    <p className="mt-4 text-slate-600 text-base leading-relaxed">{c.body}</p>
                                    <ul className="mt-5 space-y-2">
                                        {c.bullets.map((b) => (
                                            <li key={b} className="flex items-start gap-2 text-slate-700 text-sm">
                                                <i className="fa-solid fa-check text-emerald-600 text-xs mt-1.5 shrink-0" />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={openDemo}
                                        className="mt-6 text-sm text-slate-900 font-medium inline-flex items-center gap-2 hover:gap-3 transition-all"
                                    >
                                        Book a demo <i className="fa-solid fa-arrow-right text-xs" />
                                    </button>
                                </div>
                                <div className={`md:col-span-5 mesh-frame ${c.mesh} flex items-center justify-center`} style={{ borderRadius: 0, minHeight: 260 }}>
                                    <div className="glass-card p-6 w-full max-w-xs">
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
                                            {c.pill} · Live
                                        </div>
                                        <div className="space-y-2 text-xs">
                                            {c.bullets.map((b, idx) => (
                                                <div
                                                    key={idx}
                                                    className={
                                                        idx === c.bullets.length - 1
                                                            ? "bg-slate-900 text-white rounded px-2.5 py-1.5"
                                                            : "bg-white/80 rounded px-2.5 py-1.5 text-slate-700 border border-white/60"
                                                    }
                                                >
                                                    {b}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

/* ------------------------------------------------------------------
   ONE CORE — 6 platform pillars
------------------------------------------------------------------- */
const CORE_PILLARS = [
    { icon: "fa-solid fa-brain", title: "LLM-agnostic engine", body: "Bring your model of choice — or let Nyra route each task to the best model for accuracy, latency and cost." },
    { icon: "fa-solid fa-book-open", title: "Enterprise knowledge (RAG)", body: "Ground every answer in your documents, policies and data with retrieval, citations and freshness controls." },
    { icon: "fa-solid fa-shield-halved", title: "Guardrails & governance", body: "PII redaction, policy filters, role-based access and human approval gates on every autonomous action." },
    { icon: "fa-solid fa-plug", title: "Integrations", body: "200+ connectors for CRM, ERP, ITSM, core banking, e-procurement portals and telephony — plus open APIs." },
    { icon: "fa-solid fa-chart-column", title: "Analytics & insights", body: "Containment, CSAT, agent QA, bid win-rates and ROI dashboards in one console." },
    { icon: "fa-solid fa-server", title: "Deploy anywhere", body: "SaaS, private cloud, VPC or fully on-premise — with data residency and encryption in transit and at rest." },
];

export const OneCore = () => (
    <section className="py-24 md:py-28 bg-mint" data-testid="one-core">
        <div className="container-x">
            <Reveal>
                <div className="mb-14 max-w-3xl">
                    <div className="text-eyebrow text-slate-500 mb-3">The Nyra Core</div>
                    <h2 className="heading-section text-3xl md:text-5xl">
                        One core <span className="italic">underneath everything.</span>
                    </h2>
                </div>
            </Reveal>
            <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {CORE_PILLARS.map((p) => (
                    <StaggerChild key={p.title}>
                        <div className="gleap-card p-6 h-full" data-testid={`core-pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            <div
                                className="icon-sticker w-11 h-11 mb-4"
                                style={{ "--st-color": "#235F52" }}
                            >
                                <i className={p.icon} />
                            </div>
                            <div className="text-slate-900 font-medium">{p.title}</div>
                            <p className="mt-2 text-slate-600 text-sm leading-relaxed">{p.body}</p>
                        </div>
                    </StaggerChild>
                ))}
            </StaggerGroup>
        </div>
    </section>
);

/* ------------------------------------------------------------------
   WHY NYRA — 6 reasons
------------------------------------------------------------------- */
const WHY_NYRA = [
    { title: "One platform, not five vendors", body: "Chat, voice, agents, orchestration and tender intelligence share one core, one console and one contract.", icon: "fa-solid fa-cubes", accent: "#2E7D6B" },
    { title: "Multilingual by design", body: "22+ languages including deep support for Indian languages — across text and voice, not just translation.", icon: "fa-solid fa-language", accent: "#5FA88A" },
    { title: "Autonomy with accountability", body: "Every agent action is logged, explainable and reversible. Approvals where you want them, autopilot where you don't.", icon: "fa-solid fa-scale-balanced", accent: "#235F52" },
    { title: "Enterprise-grade security", body: "Encryption at rest and in transit, role-based access, PII redaction and flexible data-residency options.", icon: "fa-solid fa-shield-halved", accent: "#2E7D6B" },
    { title: "Weeks to value", body: "Prebuilt agents, connectors and industry templates mean pilots in days and production in weeks.", icon: "fa-solid fa-rocket", accent: "#5FA88A" },
    { title: "Proven in procurement", body: "TenderIQ was built and battle-tested on real, complex tenders — not adapted from a generic document tool.", icon: "fa-solid fa-file-signature", accent: "#235F52" },
];

export const WhyNyra = () => (
    <section className="py-24 md:py-28 bg-clean relative overflow-hidden" data-testid="why-nyra">
        {/* subtle mesh backdrop */}
        <div
            className="absolute inset-0 pointer-events-none opacity-60"
            style={{
                background:
                    "radial-gradient(60% 40% at 15% 20%, rgba(11,99,206,0.05), transparent 60%), radial-gradient(50% 40% at 85% 80%, rgba(16,185,129,0.06), transparent 60%)",
            }}
        />
        <div className="container-x relative">
            <Reveal>
                <div className="mb-16 max-w-3xl">
                    <div className="inline-flex items-center gap-2 text-xs font-medium tracking-widest uppercase text-slate-500 mb-4">
                        <span className="w-6 h-px bg-slate-400" /> Why Nyra
                    </div>
                    <h2 className="heading-section text-3xl md:text-5xl">
                        Why teams <span className="italic">consolidate</span> on Nyra AI.
                    </h2>
                    <p className="mt-5 text-intro text-slate-600 text-base md:text-lg">
                        Six reasons enterprises pick Nyra over stitching together five separate vendors —
                        one console, one contract, one accountable AI partner.
                    </p>
                </div>
            </Reveal>

            {/* Clean 3-column grid — no giant orbs, unified light styling */}
            <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {WHY_NYRA.map((w, i) => (
                    <StaggerChild key={w.title}>
                        <motion.div
                            whileHover={{ y: -4 }}
                            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                            className="h-full rounded-3xl p-8 md:p-9 relative overflow-hidden group border border-slate-200 hover:border-slate-300 transition-all"
                            style={{
                                background:
                                    "linear-gradient(135deg, #ffffff 0%, #fdfdfd 100%)",
                                boxShadow: "0 4px 12px -6px rgba(30,58,95,0.06)",
                            }}
                            data-testid={`why-${i}`}
                        >
                            {/* accent gradient bar top */}
                            <div
                                className="absolute top-0 left-0 h-1 transition-all duration-500 group-hover:w-full"
                                style={{
                                    width: "48px",
                                    background: `linear-gradient(90deg, ${w.accent}, ${w.accent}00)`,
                                }}
                            />

                            <div className="flex items-start justify-between gap-4 mb-8">
                                <div
                                    className="icon-sticker w-14 h-14 shrink-0"
                                    style={{ "--st-color": w.accent }}
                                >
                                    <i className={`${w.icon} text-xl`} />
                                </div>
                                <div
                                    className="heading-stat text-5xl md:text-6xl text-slate-300"
                                >
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                            </div>

                            <h3
                                className="heading-section text-2xl md:text-3xl text-slate-900"
                            >
                                {w.title}
                            </h3>
                            <p className="mt-4 text-sm md:text-base leading-relaxed text-slate-600">
                                {w.body}
                            </p>

                            {/* corner ornament — small dot pattern */}
                            <div
                                className="absolute bottom-6 right-6 flex gap-1.5 opacity-40"
                                aria-hidden
                            >
                                <span
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ background: w.accent }}
                                />
                                <span
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ background: w.accent, opacity: 0.6 }}
                                />
                                <span
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ background: w.accent, opacity: 0.3 }}
                                />
                            </div>
                        </motion.div>
                    </StaggerChild>
                ))}
            </StaggerGroup>
        </div>
    </section>
);

